import { GridCellParams, GridValidRowModel } from '@mui/x-data-grid-pro'
import SingleDropdown, {
  DropdownValue,
} from 'ext-ui/components/Dropdown/SingleDropdown/SingleDropdown'
import { input_borderHover } from 'ext-ui/style/colors'
import React from 'react'
import FinalApprovalApprovedIcon from 'src/assets/svgs/FinalApprovalApprovedIcon.svg'
import FinalApprovalFlaggedIcon from 'src/assets/svgs/FinalApprovalFlaggedIcon.svg'
import FinalApprovalPendingIcon from 'src/assets/svgs/FinalApprovalPendingIcon.svg'
import DataGridCellWrapper from 'src/components/DataGrid/cells/DataGridCellWrapper/DataGridCellWrapper'
import { TransactionsFiltersBody } from 'src/components/TransactionsListFilter/transactionsFilter.schema'
import { useTranslate } from 'src/localization/Trns'
import { TransactionFinalApprovalStatus } from 'src/models/transaction.models'
import { usePatchTransactionFinalApprovalStatus } from 'src/queries/expenseReview.queries'

type FinalApprovalDropdownCellProps<Row extends GridValidRowModel> = {
  filters: TransactionsFiltersBody
  params: GridCellParams<Row>
  status?: TransactionFinalApprovalStatus
}

const presentationForStatus = {
  [TransactionFinalApprovalStatus.PendingReview]: {
    label: 'Pending',
    icon: FinalApprovalPendingIcon,
  },
  [TransactionFinalApprovalStatus.Flagged]: {
    label: 'Flagged',
    icon: FinalApprovalFlaggedIcon,
  },
  [TransactionFinalApprovalStatus.Reviewed]: {
    label: 'Approved',
    icon: FinalApprovalApprovedIcon,
  },
}

function FinalApprovalDropdownCell<Row extends GridValidRowModel>({
  filters,
  params,
  status = TransactionFinalApprovalStatus.PendingReview,
}: FinalApprovalDropdownCellProps<Row>): React.ReactElement {
  const translate = useTranslate()
  const { mutateAsync: patchFinalApprovalStatus } =
    usePatchTransactionFinalApprovalStatus()
  const transactionId = params.row.transaction.id
  const selected = presentationForStatus[status]
  const options = Object.entries(presentationForStatus).map(
    ([value, presentation]) => ({
      value,
      label: translate(presentation.label),
      icon: <img alt="" height={16} src={presentation.icon} width={16} />,
    }),
  )

  const handleSelect = (selectedValue: DropdownValue): Promise<void> =>
    patchFinalApprovalStatus({
      body: {
        transactionIds: [transactionId],
        finalApprovalStatus:
          selectedValue.value as TransactionFinalApprovalStatus,
      },
      filters,
    }).then(() => undefined)

  const stopPropagation = (event: React.SyntheticEvent): void => {
    event.stopPropagation()
  }

  return (
    <DataGridCellWrapper
      compact
      params={params}
      onClick={stopPropagation}
      onFocus={stopPropagation}
      onKeyDown={stopPropagation}
    >
      <SingleDropdown
        clearable={false}
        containerStyle={{
          alignItems: 'center',
          borderColor: input_borderHover,
          borderRadius: 4,
          boxSizing: 'border-box',
          display: 'flex',
          flexShrink: 0,
          gap: 4,
          height: 32,
          justifyContent: 'space-between',
          overflow: 'hidden',
          padding: '4px 8px',
          width: 144,
        }}
        data-testid={`final-approval-${transactionId}`}
        dropdownContainer={document.body}
        dropdownOptionsCount={3}
        hideSelectedIcon
        iconLeft={
          <img
            alt=""
            data-testid={`final-approval-${transactionId}-status-icon`}
            height={16}
            src={selected.icon}
            width={16}
          />
        }
        noneLabel=""
        onSelect={handleSelect}
        options={options}
        searchable={false}
        selectedValue={{
          value: status,
          label: translate(selected.label),
        }}
        style={{ minWidth: 0, padding: 0 }}
        variant="small"
      />
    </DataGridCellWrapper>
  )
}

export default FinalApprovalDropdownCell
